import { useState } from 'react';
import { FaInfoCircle } from 'react-icons/fa';
import { Input } from '../../../components/forms/Input';
import { Select } from '../../../components/forms/Select';
import { Textarea } from '../../../components/forms/Textarea';
import { CANCELLATION_REASONS } from '../constants';
import { usePasStore } from '../pasStore';
import type { Policy } from '../types';
import { Button } from './Button';
import { Modal } from './Modal';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  policy: Policy;
}

export const CancelPolicyModal: React.FC<Props> = ({ isOpen, onClose, policy }) => {
  const cancelPolicy = usePasStore((s) => s.cancelPolicy);
  const [reason, setReason] = useState(CANCELLATION_REASONS[0]);
  const [note, setNote] = useState('');
  const [endDate, setEndDate] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const confirmed = confirmText.trim().toUpperCase() === 'CANCEL' && !!endDate;

  const handleSubmit = async () => {
    if (!confirmed) return;
    setSubmitting(true);
    try {
      await cancelPolicy(policy.id, reason, note.trim(), endDate);
      onClose();
    } catch {
      // The store surfaces the error as a toast; keep the modal open for a retry.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Cancel policy"
      subtitle={`${policy.policyNumber} · ${policy.insured.name}`}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Keep policy
          </Button>
          <Button variant="danger" onClick={handleSubmit} loading={submitting} disabled={!confirmed}>
            Cancel policy
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div
          className="flex items-start gap-3 rounded-lg border p-3"
          style={{ backgroundColor: 'var(--color-background-paper)', borderColor: 'var(--color-border-light)' }}
        >
          <FaInfoCircle className="mt-0.5 flex-shrink-0" style={{ color: 'var(--color-text-secondary)' }} />
          <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            Cancellation date is recorded in the audit timeline
          </p>
        </div>

        <Input
          label="Cancellation end date"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          fullWidth
          required
        />
        <Select
          label="Cancellation reason"
          options={CANCELLATION_REASONS.map((r) => ({ value: r, label: r }))}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          required
        />
        <Textarea
          label="Internal note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          placeholder="Add context for the audit trail (optional)…"
          fullWidth
        />
        <div>
          <label className="mb-1 block text-sm font-medium" style={{ color: 'var(--color-text-primary)' }} htmlFor="cancel-confirm">
            Type <span className="font-bold">CANCEL</span> to confirm
          </label>
          <input
            id="cancel-confirm"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1"
            style={{
              backgroundColor: 'var(--color-background-default)',
              borderColor: 'var(--color-border-light)',
              color: 'var(--color-text-primary)',
              ['--tw-ring-color' as string]: 'var(--color-error-500)',
            }}
          />
        </div>
      </div>
    </Modal>
  );
};
